"use client"

import { useState } from "react"
import { Link } from "react-router-dom"
import { Button } from "../ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { Textarea } from "../ui/textarea"
import { ArrowLeft, Car, Upload, X } from "lucide-react"

import { db, storage } from "../../firebaseconfig"; 
import { collection, addDoc } from "firebase/firestore";
import uploadService from "../../services/uploadService";
import { quickDiagnostic } from "../../services/firebaseDiagnostics";

export default function CreditoPrendarioForm() {
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "",
    dni: "",
    ingresos: "",
    tipoVehiculo: "",
    marca: "",
    modelo: "",
    año: "",
    montoSolicitado: "",
    plazo: "",
    observaciones: "",
    estado: "pendiente",
    fechaCreacion: new Date().toISOString(),
  })

  const [archivos, setArchivos] = useState({
    reciboSueldo: [],
    fotoDNI: []
  })

  const [uploading, setUploading] = useState(false)

  // Función para subir archivos usando el servicio mejorado
  const subirArchivos = async (archivos, tipoDocumento) => {
    if (!archivos || archivos.length === 0) {
      return []
    }

    try {
      console.log(`📋 Subiendo ${archivos.length} archivos de tipo: ${tipoDocumento}`)
      
      // Validar archivos antes de subirlos
      const erroresValidacion = uploadService.validarArchivos(
        archivos, 
        ['pdf', 'jpg', 'jpeg', 'png'], // tipos permitidos
        10 * 1024 * 1024 // 10MB máximo
      )

      if (erroresValidacion.length > 0) {
        throw new Error(`Errores de validación:\n${erroresValidacion.join('\n')}`)
      }

      // Usar el servicio de subida mejorado
      const ruta = `credito-prendario/${tipoDocumento}`
      const resultado = await uploadService.subirMultiplesArchivos(archivos, ruta)
      
      if (resultado.errores.length > 0) {
        console.warn('⚠️ Algunos archivos no se pudieron subir:', resultado.errores)
        // Si hay errores pero también éxitos, mostrar advertencia
        if (resultado.exitosos.length > 0) {
          alert(`⚠️ Se subieron ${resultado.exitosos.length} de ${resultado.total} archivos.\nErrores: ${resultado.errores.map(e => e.error).join(', ')}`)
        }
      }

      if (resultado.exitosos.length === 0) {
        throw new Error('No se pudo subir ningún archivo')
      }

      console.log(`✅ Se subieron ${resultado.exitosos.length} archivos exitosamente`)
      return resultado.exitosos

    } catch (error) {
      console.error('❌ Error en subirArchivos:', error)
      throw error
    }
  }

  const enviarSolicitudAFirebase = async (data) => {
    try {
      setUploading(true);
      console.log("Iniciando envío de solicitud...");
      
      // Verificar conexión a Firebase
      console.log("Configuración de Storage:", {
        bucket: storage.app.options.storageBucket,
        proyecto: storage.app.options.projectId
      });
      
      // Subir archivos usando el servicio mejorado
      const urls = {};
      
      // Subir recibos de sueldo
      if (archivos.reciboSueldo.length > 0) {
        console.log(`📄 Subiendo ${archivos.reciboSueldo.length} recibos de sueldo...`);
        const resultadoRecibos = await subirArchivos(archivos.reciboSueldo, 'recibos-sueldo');
        urls.reciboSueldoURLs = resultadoRecibos.map(r => r.url);
        console.log(`✅ ${resultadoRecibos.length} recibos subidos exitosamente`);
      }
      
      // Subir fotos de DNI
      if (archivos.fotoDNI.length > 0) {
        console.log(`🆔 Subiendo ${archivos.fotoDNI.length} fotos de DNI...`);
        const resultadoDNI = await subirArchivos(archivos.fotoDNI, 'fotos-dni');
        urls.fotoDNIURLs = resultadoDNI.map(r => r.url);
        console.log(`✅ ${resultadoDNI.length} fotos de DNI subidas exitosamente`);
      }

      console.log("Todos los archivos subidos, guardando en Firestore...");
      
      // Guardar en Firestore con las URLs de los archivos
      const solicitudData = {
        ...data,
        archivos: {
          reciboSueldo: archivos.reciboSueldo.map(file => file.name),
          fotoDNI: archivos.fotoDNI.map(file => file.name),
          ...urls // Agregar las URLs de descarga
        }
      };
      
      await addDoc(collection(db, "creditosPrendarios"), solicitudData);
      console.log("Solicitud guardada en Firestore exitosamente");
      alert("Solicitud enviada correctamente. Nos contactaremos contigo pronto.");
      
      // Limpiar formulario
      setFormData({
        nombre: "",
        email: "",
        telefono: "",
        dni: "",
        ingresos: "",
        tipoVehiculo: "",
        marca: "",
        modelo: "",
        año: "",
        montoSolicitado: "",
        plazo: "",
        observaciones: "",
        estado: "pendiente",
        fechaCreacion: new Date().toISOString(),
      });
      setArchivos({ reciboSueldo: [], fotoDNI: [] });
      
    } catch (error) {
      console.error("Error completo:", {
        message: error.message,
        code: error.code,
        stack: error.stack,
        details: error
      });
      
      let errorMessage = "Error desconocido al enviar la solicitud.";
      
      if (error.message.includes("CORS")) {
        errorMessage = "Error de configuración del servidor. Por favor, contacta al administrador.";
      } else if (error.message.includes("network")) {
        errorMessage = "Error de conexión. Verifica tu conexión a internet.";
      } else if (error.message.includes("permission")) {
        errorMessage = "Error de permisos. Por favor, contacta al administrador.";
      } else if (error.message.includes("subiendo")) {
        errorMessage = error.message; // Usar el mensaje específico del archivo
      } else {
        errorMessage = `Error: ${error.message}`;
      }
      
      alert(`❌ ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que al menos la foto de DNI esté presente
    if (archivos.fotoDNI.length === 0) {
      alert("❌ Por favor sube la Foto de DNI (documento obligatorio)");
      return;
    }
    
    // Validar recibo de sueldo (opcional pero informar al usuario)
    if (archivos.reciboSueldo.length === 0) {
      const confirmar = window.confirm("No has subido recibo de sueldo. ¿Deseas continuar sin este documento? Podrás subirlo más tarde si es necesario.");
      if (!confirmar) {
        return;
      }
    }
    
    await enviarSolicitudAFirebase(formData);
  }

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleFileChange = (tipo, files) => {
    if (files && files.length > 0) {
      const validFiles = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Validar tipo de archivo
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg']
        if (!allowedTypes.includes(file.type)) {
          alert(`El archivo ${file.name} no es válido. Solo se permiten archivos PDF, JPG, JPEG o PNG`)
          continue;
        }
        
        // Validar tamaño (máximo 5MB)
        if (file.size > 5 * 1024 * 1024) {
          alert(`El archivo ${file.name} supera los 5MB permitidos`)
          continue;
        }
        
        validFiles.push(file);
      }
      
      if (validFiles.length > 0) {
        setArchivos(prev => ({ 
          ...prev, 
          [tipo]: [...prev[tipo], ...validFiles]
        }));
      }
    }
  }

  const removeFile = (tipo, index) => {
    setArchivos(prev => ({
      ...prev,
      [tipo]: prev[tipo].filter((_, i) => i !== index)
    }));
  }

  const FileUpload = ({ label, tipo, required = false, maxFiles = 5, description }) => (
    <div className="space-y-2">
      <Label htmlFor={tipo}>
        {label} {required && '*'}
        <span className="text-sm text-gray-500 ml-2">
          ({archivos[tipo].length}/{maxFiles} archivos)
        </span>
      </Label>
      
      {/* Mostrar archivos seleccionados */}
      {archivos[tipo].length > 0 && (
        <div className="space-y-2 mb-4">
          {archivos[tipo].map((file, index) => (
            <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded border">
              <div className="flex items-center gap-2">
                <Upload className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-gray-700">{file.name}</span>
                <span className="text-xs text-gray-500">
                  ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </span>
              </div>
              <button
                type="button"
                onClick={() => removeFile(tipo, index)}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Zona de subida */}
      {archivos[tipo].length < maxFiles && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-1">
            {description || "Arrastra y suelta tus archivos aquí, o haz clic para seleccionar"}
          </p>
          <p className="text-xs text-gray-400 mb-2">
            PDF, JPG, JPEG o PNG (máx. 5MB cada uno)
          </p>
          <p className="text-xs text-gray-500 mb-3">
            Puedes subir hasta {maxFiles} archivos
          </p>
          <input
            id={tipo}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            multiple
            onChange={(e) => handleFileChange(tipo, e.target.files)}
            className="hidden"
          />
          <label
            htmlFor={tipo}
            className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer"
          >
            Seleccionar archivos
          </label>
        </div>
      )}
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-100 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al inicio
            </Link>
          </Button>
        </div>

        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Car className="w-8 h-8 text-blue-600" />
            </div>
            <CardTitle className="text-3xl">Solicitud de Crédito Prendario</CardTitle>
            <CardDescription>Completa el formulario para solicitar tu crédito prendario</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre Completo *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => handleChange("nombre", e.target.value)}
                    placeholder="Ingresa tu nombre completo"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dni">DNI *</Label>
                  <Input
                    id="dni"
                    value={formData.dni}
                    onChange={(e) => handleChange("dni", e.target.value)}
                    placeholder="12345678"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="tu@email.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefono">Teléfono *</Label>
                  <Input
                    id="telefono"
                    value={formData.telefono}
                    onChange={(e) => handleChange("telefono", e.target.value)}
                    placeholder="+54 11 1234-5678"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ingresos">Ingresos Mensuales *</Label>
                <Input
                  id="ingresos"
                  value={formData.ingresos}
                  onChange={(e) => handleChange("ingresos", e.target.value)}
                  placeholder="$300,000"
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tipoVehiculo">Tipo de Vehículo *</Label>
                  <Select onValueChange={(value) => handleChange("tipoVehiculo", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona el tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto</SelectItem>
                      <SelectItem value="camioneta">Camioneta</SelectItem>
                      <SelectItem value="suv">SUV</SelectItem>
                      <SelectItem value="moto">Moto</SelectItem>
                      <SelectItem value="utilitario">Utilitario</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="año">Año del Vehículo *</Label>
                  <Input
                    id="año"
                    value={formData.año}
                    onChange={(e) => handleChange("año", e.target.value)}
                    placeholder="2020"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="marca">Marca *</Label>
                  <Input
                    id="marca"
                    value={formData.marca}
                    onChange={(e) => handleChange("marca", e.target.value)}
                    placeholder="Toyota, Ford, Chevrolet, etc."
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modelo">Modelo *</Label>
                  <Input
                    id="modelo"
                    value={formData.modelo}
                    onChange={(e) => handleChange("modelo", e.target.value)}
                    placeholder="Corolla, Focus, Cruze, etc."
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="montoSolicitado">Monto Solicitado *</Label>
                  <Input
                    id="montoSolicitado"
                    value={formData.montoSolicitado}
                    onChange={(e) => handleChange("montoSolicitado", e.target.value)}
                    placeholder="$5,000,000"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="plazo">Plazo (años) *</Label>
                  <Select onValueChange={(value) => handleChange("plazo", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona el plazo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 año</SelectItem>
                      <SelectItem value="2">2 años</SelectItem>
                      <SelectItem value="3">3 años</SelectItem>
                      <SelectItem value="4">4 años</SelectItem>
                      <SelectItem value="5">5 años</SelectItem>
                      <SelectItem value="6">6 años</SelectItem>
                      <SelectItem value="7">7 años</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Sección de documentos */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 text-blue-700">Documentación Requerida</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <FileUpload 
                    label="Recibos de Sueldo" 
                    tipo="reciboSueldo" 
                    required={true}
                    maxFiles={3}
                    description="Sube hasta 3 recibos de sueldo (últimos meses)"
                  />
                  <FileUpload 
                    label="Fotos DNI" 
                    tipo="fotoDNI" 
                    required={true}
                    maxFiles={2}
                    description="Sube fotos del anverso y reverso de tu DNI"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="observaciones">Observaciones</Label>
                <Textarea
                  id="observaciones"
                  value={formData.observaciones}
                  onChange={(e) => handleChange("observaciones", e.target.value)}
                  placeholder="Información adicional sobre el vehículo o tu situación"
                  className="min-h-[100px]"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={uploading}
              >
                {uploading ? "Enviando solicitud..." : "Enviar Solicitud de Crédito Prendario"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}